import React from "react";
import { Helmet } from "react-helmet-async";
import {
  SITE_NAME,
  SITE_URL,
  LOCALE,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE_ALT,
  absoluteUrl,
  baseSchemaGraph,
  resolveSocialImage,
} from "../seo/siteMeta.mjs";

/**
 * Compose the document title. Pages pass their bare title ("About Dark
 * Defensive"); the brand is appended unless it is already present.
 */
export function buildTitle(title) {
  if (!title) return DEFAULT_TITLE;
  const clean = String(title).replace(/\s+/g, " ").trim();
  if (!clean) return DEFAULT_TITLE;
  return clean.toLowerCase().includes("dark defensive")
    ? clean
    : `${clean} | ${SITE_NAME}`;
}

/**
 * Per-page head tags: title, description, canonical, robots, Open Graph
 * (LinkedIn / Facebook / Slack), Twitter cards, and JSON-LD structured data.
 *
 * The same tags are baked into the prerendered HTML by scripts/prerender-seo.mjs
 * so that crawlers which do not execute JavaScript still see them. Those static
 * tags carry `data-rh="true"`, which lets react-helmet-async take ownership of
 * them on mount instead of duplicating them.
 *
 * @param {object}   props
 * @param {string}   props.title          Page title, brand appended automatically.
 * @param {string}   props.description    Meta description (~150-160 chars).
 * @param {string}   props.path           Site-relative canonical path, e.g. "/about".
 * @param {string}   [props.image]        Social image URL (absolute or site-relative).
 * @param {string}   [props.imageAlt]     Alt text for the social image.
 * @param {string}   [props.type]         Open Graph type: "website" | "article".
 * @param {boolean}  [props.noindex]      Keep the page out of search results.
 * @param {string}   [props.publishedTime] ISO date, articles only.
 * @param {string}   [props.modifiedTime]  ISO date, articles only.
 * @param {string}   [props.author]        Author name, articles only.
 * @param {string[]} [props.sections]      Article sections / categories.
 * @param {object[]} [props.schema]        Extra JSON-LD nodes to merge into the graph.
 */
const Seo = ({
  title,
  description,
  path = "/",
  image,
  imageAlt,
  type = "website",
  noindex = false,
  publishedTime,
  modifiedTime,
  author,
  sections,
  schema = [],
}) => {
  const fullTitle = buildTitle(title);
  const metaDescription = (description || DEFAULT_DESCRIPTION).trim();
  const canonical = absoluteUrl(path);
  const ogImage = resolveSocialImage(image);
  const ogImageAlt = imageAlt || DEFAULT_OG_IMAGE_ALT;
  const robots = noindex
    ? "noindex, nofollow"
    : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

  const graph = [...baseSchemaGraph, ...schema];
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": graph.map((node) =>
      // Give every page-level node a stable @id anchored to its canonical URL.
      node["@id"] ? node : { ...node, "@id": `${canonical}#${node["@type"]}` }
    ),
  };

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />

      {/* Open Graph — used by LinkedIn, Facebook, Slack, WhatsApp, Teams */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={LOCALE} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage.url} />
      <meta property="og:image:secure_url" content={ogImage.url} />
      {ogImage.type && <meta property="og:image:type" content={ogImage.type} />}
      {ogImage.width && (
        <meta property="og:image:width" content={String(ogImage.width)} />
      )}
      {ogImage.height && (
        <meta property="og:image:height" content={String(ogImage.height)} />
      )}
      <meta property="og:image:alt" content={ogImageAlt} />

      {/* LinkedIn reads these author/publisher hints on article shares */}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && author && (
        <meta property="article:author" content={author} />
      )}
      {type === "article" && (
        <meta property="article:publisher" content={SITE_URL} />
      )}
      {type === "article" &&
        (sections || []).map((s) => (
          <meta property="article:section" content={s} key={s} />
        ))}

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage.url} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export default Seo;
